import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  useColorMode,
  Divider,
  Button,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { NavbarWithCallToAction } from '../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../brandConfig';

interface TimelineItemProps {
  quarter: string;
  year: string;
  title: string;
  status: 'completed' | 'in-progress' | 'upcoming';
  description: string;
  highlights: string[];
  colorMode: 'light' | 'dark';
}

const TimelineItem = ({ quarter, year, title, status, description, highlights, colorMode }: TimelineItemProps) => {
  const statusColors = {
    completed: '#10b981',
    'in-progress': '#3b82f6',
    upcoming: '#f59e0b', // Amber/gold for future items
  };

  const statusLabels = {
    completed: 'Completed',
    'in-progress': 'In Progress',
    upcoming: 'Upcoming',
  };

  return (
    <Box
      position="relative"
      pl={{ base: 8, md: 12 }}
      pb={12}
      borderLeft="2px solid"
      borderColor={statusColors[status]}
      _last={{ borderLeft: 'none', pb: 0 }}
    >
      {/* Timeline dot */}
      <Box
        position="absolute"
        left="-6px"
        top="0"
        w="10px"
        h="10px"
        borderRadius="full"
        bg={statusColors[status]}
        boxShadow={`0 0 20px ${statusColors[status]}`}
      />

      <VStack align="start" spacing={4}>
        <HStack spacing={4} flexWrap="wrap">
          <Badge
            colorScheme="blue"
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
            bg={`${statusColors[status]}20`}
            color={statusColors[status]}
            border="1px solid"
            borderColor={statusColors[status]}
          >
            {quarter} {year}
          </Badge>
          <Badge
            fontSize="xs"
            px={2}
            py={1}
            borderRadius="full"
            bg={`${statusColors[status]}10`}
            color={statusColors[status]}
          >
            {statusLabels[status]}
          </Badge>
        </HStack>

        <Heading size="md" color={getColor('text.primary', colorMode)}>
          {title}
        </Heading>

        <Text color={getColor('text.secondary', colorMode)} fontSize="md">
          {description}
        </Text>

        <VStack align="start" spacing={2} w="full">
          {highlights.map((highlight, idx) => (
            <HStack key={idx} align="start">
              <Text color={statusColors[status]} fontSize="lg">•</Text>
              <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                {highlight}
              </Text>
            </HStack>
          ))}
        </VStack>
      </VStack>
    </Box>
  );
};

export const TimelinePage = () => {
  const { colorMode } = useColorMode();

  const timeline = [
    {
      quarter: 'Q1',
      year: '2024',
      title: 'Concept & Proof of Concept',
      status: 'completed' as const,
      description: 'The vision for Block52 was born. We developed the initial concept for a decentralized poker infrastructure and built our first proof of concept.',
      highlights: [
        'Poker Virtual Machine (PVM) concept designed',
        'Initial architecture planning for blockchain poker',
        'Proof of concept demonstrating core game mechanics',
        'Foundation team assembled',
      ],
    },
    {
      quarter: 'Q2',
      year: '2024',
      title: 'Custom Blockchain Development',
      status: 'completed' as const,
      description: 'Built our first blockchain implementation in TypeScript, laying the groundwork for the Poker Virtual Machine infrastructure.',
      highlights: [
        'Custom blockchain developed in TypeScript',
        'Initial consensus mechanism implemented',
        'Smart contract framework established',
        'Basic validator network prototype',
      ],
    },
    {
      quarter: 'Q3',
      year: '2024',
      title: 'Blockchain Completion & Layer Architecture',
      status: 'completed' as const,
      description: 'Finalized the custom blockchain and began developing the layered architecture that would become the foundation of Block52.',
      highlights: [
        'TypeScript blockchain implementation completed',
        'Layer architecture designed and developed',
        'Initial game state management system',
        'Foundation for Club-in-a-Box platform',
      ],
    },
    {
      quarter: 'Q4',
      year: '2024',
      title: 'Platform Development',
      status: 'completed' as const,
      description: 'Continued building out the layer infrastructure and began planning the migration to Cosmos SDK for production scalability.',
      highlights: [
        'Club operator dashboard development',
        'Player management systems',
        'Agent network infrastructure',
        'Cosmos SDK migration planning initiated',
      ],
    },
    {
      quarter: 'Q1',
      year: '2025',
      title: 'Platform Refinement',
      status: 'completed' as const,
      description: 'Refined the platform layers and prepared for the strategic pivot to Cosmos SDK for enhanced scalability and interoperability.',
      highlights: [
        'Layer architecture optimization',
        'Club-in-a-Box platform features completed',
        'Business development tools finalized',
        'Cosmos migration architecture designed',
      ],
    },
    {
      quarter: 'Q2',
      year: '2025',
      title: 'Cosmos Migration Begins',
      status: 'completed' as const,
      description: 'Started migrating the first card game implementation to Cosmos SDK, marking a major milestone in production readiness.',
      highlights: [
        'First card game migrated to Cosmos SDK',
        'Validator node infrastructure rebuilt on Cosmos',
        'Inter-Blockchain Communication (IBC) integration',
        'Performance testing and optimization',
      ],
    },
    {
      quarter: 'Q3',
      year: '2025',
      title: 'Alpha Development',
      status: 'completed' as const,
      description: 'Deep development phase focusing on completing the Cosmos-based PVM alpha release with full game mechanics and security features.',
      highlights: [
        'Texas Hold\'em implementation on Cosmos',
        'Game variants: Tournament and Sit & Go',
        'Smart contract-based rake distribution',
        'Multi-chain validator network testing',
        'Performance testing and optimization',
      ],
    },
    {
      quarter: 'Q4',
      year: '2025',
      title: 'Alpha Release Preparation',
      status: 'completed' as const,
      description: 'Final preparations for the Poker Virtual Machine alpha release on Cosmos. Security audits, testing, and operator onboarding.',
      highlights: [
        'PVM Alpha release on Cosmos',
        'Security audits and penetration testing',
        'Early operator onboarding program',
        'Testnet validator network launch',
        'Documentation and developer tools',
        'Finalized token economics',
      ],
    },
  ];

  const roadmap = [
    {
      quarter: 'Q1',
      year: '2026',
      title: 'Public Alpha & Mainnet Launch',
      status: 'upcoming' as const,
      description: 'Launch the Poker Virtual Machine on Cosmos mainnet with the first wave of poker clubs going live.',
      highlights: [
        'Mainnet launch on Cosmos',
        'First 10 poker clubs deployed',
        'B52 token launch and staking program',
        'Validator rewards program begins',
        'Global player pool activation',
      ],
    },
    {
      quarter: 'Q2',
      year: '2026',
      title: 'Ecosystem Expansion',
      status: 'upcoming' as const,
      description: 'Scale the network with additional game types, enhanced features, and expanded club operator tools.',
      highlights: [
        'Omaha and additional poker variants',
        'Tournament infrastructure',
        'Enhanced analytics dashboard',
        'Mobile app launch (iOS/Android)',
        'Cross-chain bridge to Ethereum',
      ],
    },
    {
      quarter: 'Q3',
      year: '2026',
      title: 'Advanced Features & Token Utilities',
      status: 'upcoming' as const,
      description: 'Roll out advanced features including new token mechanics, additional game types, and institutional-grade tools.',
      highlights: [
        'Advanced B52 staking tiers & rewards',
        'B52 rake-back rewards for token holders',
        'New card game variants (Blackjack, Baccarat)',
        'Game-specific token utilities',
        'Institutional poker club solutions',
        'Regulatory compliance toolkit',
      ],
    },
    {
      quarter: 'Q4',
      year: '2026',
      title: 'Global Scale & Governance',
      status: 'upcoming' as const,
      description: 'Achieve global scale with hundreds of clubs and transition to full community governance.',
      highlights: [
        'Target: 100+ active poker clubs',
        'Full DAO governance implementation',
        'Community treasury management',
        'Regional validator nodes worldwide',
        'Block52 Foundation establishment',
      ],
    },
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box
        minH="100vh"
        bg={getColor('background.main', colorMode)}
        color={getColor('text.primary', colorMode)}
        position="relative"
      >
        <Container maxW="900px" py={20}>
          {/* Header */}
          <VStack spacing={8} textAlign="center" mb={20}>
            <Text
              sx={{
                color: getColor('primary', colorMode),
                letterSpacing: '0.13em',
                textTransform: 'uppercase',
                backgroundColor: `${getColor('primary', colorMode)}20`,
                borderRadius: '50em',
                padding: '0.7em 1.3em',
                fontSize: '0.75em',
                fontWeight: 500,
                boxShadow: `0 4px 20px ${getColor('primary', colorMode)}50`,
              }}
            >
              Development Timeline
            </Text>
            <Heading
              size="2xl"
              color={getColor('text.primary', colorMode)}
            >
              Building the Future of Decentralized Poker
            </Heading>
            <Text
              fontSize="xl"
              color={getColor('text.secondary', colorMode)}
              maxW="2xl"
            >
              From concept to alpha release: Our journey building the Poker Virtual Machine on Cosmos SDK
            </Text>
          </VStack>

          {/* Project History */}
          <VStack spacing={12} align="stretch" mb={20}>
            <Box>
              <Heading size="lg" mb={8} color={getColor('text.primary', colorMode)}>
                📜 Project History
              </Heading>
              <Box>
                {timeline.map((item, idx) => (
                  <TimelineItem key={idx} {...item} colorMode={colorMode} />
                ))}
              </Box>
            </Box>

            <Divider borderColor={getColor('border.medium', colorMode)} my={8} />

            {/* Roadmap */}
            <Box>
              <Heading size="lg" mb={4} color={getColor('text.primary', colorMode)}>
                🚀 Roadmap: Where We're Going
              </Heading>
              <Text
                fontSize="md"
                color={getColor('text.secondary', colorMode)}
                mb={8}
                maxW="3xl"
              >
                Our vision for the next year: Launching the Poker Virtual Machine on mainnet,
                scaling to hundreds of clubs, and establishing Block52 as the decentralized infrastructure
                layer for poker.
              </Text>
              <Box>
                {roadmap.map((item, idx) => (
                  <TimelineItem key={idx} {...item} colorMode={colorMode} />
                ))}
              </Box>
            </Box>
          </VStack>

          {/* Current Status Banner */}
          <Box
            p={{ base: 6, md: 8 }}
            borderRadius="xl"
            border="2px solid"
            borderColor={getColor('primary', colorMode)}
            bg={`${getColor('primary', colorMode)}10`}
            textAlign="center"
          >
            <VStack spacing={4}>
              <Badge
                colorScheme="green"
                fontSize="md"
                px={4}
                py={2}
                borderRadius="full"
                bg="#10b981"
                color="white"
              >
                Alpha Complete - Preparing for Launch
              </Badge>
              <Heading size="md" color={getColor('text.primary', colorMode)}>
                Poker Virtual Machine Ready for Mainnet
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} maxW="2xl">
                Alpha development is complete! The PVM has been built on Cosmos SDK with full game mechanics,
                security features, and token economics finalized. We're now preparing for mainnet launch.
              </Text>
              <Text fontSize="sm" color={getColor('text.secondary', colorMode)} fontStyle="italic">
                Next Major Milestone: Mainnet Launch - Q1 2026
              </Text>
              <Button
                as={RouterLink}
                to="/tokenomics"
                size="md"
                variant="outline"
                borderColor={getColor('primary', colorMode)}
                color={getColor('primary', colorMode)}
                _hover={{
                  bg: `${getColor('primary', colorMode)}20`,
                }}
                mt={2}
              >
                Learn About B52 Token Economics
              </Button>
            </VStack>
          </Box>
        </Container>
      </Box>
      <FooterWithFourColumns />
    </>
  );
};

export default TimelinePage;
